import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

const control =
  'w-full rounded-md border border-border-strong bg-surface px-3 text-fg shadow-sm placeholder:text-subtle transition-colors hover:border-subtle focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-surface-2 disabled:opacity-70 aria-[invalid=true]:border-danger';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <input ref={ref} className={cn(control, 'h-[var(--control-h)] text-base md:text-sm', className)} {...p} />
));
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea ref={ref} className={cn(control, 'min-h-24 py-2 text-base md:text-sm', className)} {...p} />
));
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...p }, ref) => (
  <div className="relative">
    <select ref={ref} className={cn(control, 'h-[var(--control-h)] appearance-none pe-9 text-base md:text-sm', className)} {...p}>{children}</select>
    <ChevronDown className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-subtle" aria-hidden />
  </div>
));
Select.displayName = 'Select';

/** Amount input: decimal keyboard on phones, tabular figures, text type so the value stays a string. */
export const MoneyInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <Input ref={ref} inputMode="decimal" autoComplete="off" className={cn('tnum text-end', className)} {...p} />
));
MoneyInput.displayName = 'MoneyInput';

interface FieldProps { label: string; error?: string; hint?: string; required?: boolean; children: (id: string, describedBy: string | undefined) => ReactNode; className?: string }

/** Label + control + hint/error wired with aria-describedby. */
export function Field({ label, error, hint, required, children, className }: FieldProps) {
  const id = useId();
  const descId = error || hint ? `${id}-d` : undefined;
  return (
    <div className={cn('grid gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-fg">
        {label}{required && <span className="ms-0.5 text-danger" aria-hidden>*</span>}
      </label>
      {children(id, descId)}
      {(error || hint) && <p id={descId} className={cn('text-xs', error ? 'text-danger' : 'text-muted')} role={error ? 'alert' : undefined}>{error ?? hint}</p>}
    </div>
  );
}
